import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { ReferenceForm } from '../components/stock/ReferenceForm';
import { formatEuro, formatPct } from '../lib/format';

function fmtM2(c: number): string {
  return `${(c / 100).toFixed(2).replace('.', ',')} m²`;
}

export function ReferenceDetailPage(): JSX.Element {
  const { id } = useParams();
  const refId = id !== undefined ? Number.parseInt(id, 10) : NaN;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['references', 'detail', refId],
    queryFn: () => api.references.get(refId),
    enabled: !Number.isNaN(refId),
  });

  const update = useMutation({
    mutationFn: (body: Parameters<typeof api.references.update>[1]) =>
      api.references.update(refId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['references'] });
      setEditing(false);
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Erreur mise à jour');
    },
  });

  const remove = useMutation({
    mutationFn: () => api.references.delete(refId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['references'] });
      navigate('/references');
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        setError('Impossible de supprimer : des rouleaux existent encore.');
      } else {
        setError(err instanceof ApiError ? err.message : 'Erreur suppression.');
      }
      setDeleteOpen(false);
    },
  });

  if (Number.isNaN(refId)) return <div className="text-sm text-red-400">ID invalide.</div>;
  if (query.isLoading) return <div className="text-sm text-slate-400">Chargement…</div>;
  if (query.isError || query.data === undefined)
    return <div className="text-sm text-red-400">Référence introuvable.</div>;

  const r = query.data.reference;

  if (editing) {
    return (
      <div className="max-w-5xl">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-slate-100">Modifier la référence</h1>
        </header>
        {error !== null && (
          <div className="mb-4 text-xs text-red-300 bg-red-900/20 border border-red-900/50 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <ReferenceForm
          initial={r}
          submitting={update.isPending}
          submitLabel="Enregistrer"
          onSubmit={(b): void => update.mutate(b)}
          onCancel={(): void => setEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className="max-w-6xl">
      <header className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">{r.nom}</h1>
          {r.code !== null && <p className="text-sm text-slate-400">{r.code}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/references/${String(r.id)}/rouleaux`}>
            <Button variant="secondary">Voir rouleaux</Button>
          </Link>
          <Button variant="secondary" onClick={(): void => setEditing(true)}>
            Modifier
          </Button>
          <Button variant="danger" onClick={(): void => setDeleteOpen(true)}>
            Supprimer
          </Button>
        </div>
      </header>

      {error !== null && (
        <div className="mb-4 text-xs text-red-300 bg-red-900/20 border border-red-900/50 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        <KpiBox
          label="Stock dispo"
          value={fmtM2(r.stock_dispo_centi_m2)}
          hint={`${String(r.nb_rouleaux_dispo)} rouleau${r.nb_rouleaux_dispo > 1 ? 'x' : ''}`}
          alerte={r.alerte_sous_seuil}
        />
        <KpiBox
          label="Coût moyen pondéré"
          value={
            r.cout_moyen_pondere_centimes_par_m2 !== null
              ? `${formatEuro(r.cout_moyen_pondere_centimes_par_m2)}/m²`
              : '—'
          }
        />
        <KpiBox label="Valeur stock" value={formatEuro(r.valeur_stock_centimes)} />
        <KpiBox
          label="Seuil alerte"
          value={r.seuil_alerte_centi_m2 > 0 ? fmtM2(r.seuil_alerte_centi_m2) : '—'}
        />
      </div>

      <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 mb-4">
        <h2 className="text-sm font-semibold text-slate-200 mb-3">Tarifs et marges</h2>
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-400 uppercase">
            <tr>
              <th className="text-left pb-2">Segment</th>
              <th className="text-right pb-2">Prix €/m²</th>
              <th className="text-right pb-2">Marge cible</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            <tr>
              <td className="py-2 text-slate-300">Pro</td>
              <td className="py-2 text-right text-slate-200">
                {formatEuro(r.prix_pro_centimes_par_m2)}
              </td>
              <td className="py-2 text-right text-slate-400">
                {r.marge_cible_pro_pct !== null ? formatPct(r.marge_cible_pro_pct) : '—'}
              </td>
            </tr>
            <tr>
              <td className="py-2 text-slate-300">Revendeur</td>
              <td className="py-2 text-right text-slate-200">
                {formatEuro(r.prix_revendeur_centimes_par_m2)}
              </td>
              <td className="py-2 text-right text-slate-400">
                {r.marge_cible_revendeur_pct !== null
                  ? formatPct(r.marge_cible_revendeur_pct)
                  : '—'}
              </td>
            </tr>
            <tr>
              <td className="py-2 text-slate-300">Particulier</td>
              <td className="py-2 text-right text-slate-200">
                {formatEuro(r.prix_particulier_centimes_par_m2)}
              </td>
              <td className="py-2 text-right text-slate-400">
                {r.marge_cible_particulier_pct !== null
                  ? formatPct(r.marge_cible_particulier_pct)
                  : '—'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {r.description !== null && r.description !== '' && (
        <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
          <div className="text-xs text-slate-500 mb-1">Description</div>
          <div className="text-sm text-slate-200 whitespace-pre-wrap">{r.description}</div>
        </div>
      )}

      <Modal
        isOpen={deleteOpen}
        onClose={(): void => setDeleteOpen(false)}
        title="Supprimer cette référence ?"
        footer={
          <>
            <Button variant="secondary" type="button" onClick={(): void => setDeleteOpen(false)}>
              Annuler
            </Button>
            <Button
              variant="danger"
              type="button"
              onClick={(): void => remove.mutate()}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Suppression…' : 'Supprimer'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-300">
          La suppression échouera si des rouleaux existent encore pour cette référence.
        </p>
      </Modal>
    </div>
  );
}

function KpiBox({
  label,
  value,
  hint,
  alerte,
}: {
  label: string;
  value: string;
  hint?: string;
  alerte?: boolean;
}): JSX.Element {
  return (
    <div
      className={`border rounded-xl p-4 ${alerte === true ? 'bg-red-900/20 border-red-700/50' : 'bg-slate-900/40 border-slate-800'}`}
    >
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div
        className={`text-xl font-semibold mt-1 ${alerte === true ? 'text-red-300' : 'text-slate-100'}`}
      >
        {value}
      </div>
      {hint !== undefined && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}
